import { useEffect, useRef } from 'react';
import { BarChart, LineChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import { init, use, type EChartsCoreOption, type ECElementEvent } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import type { AnalyticsEventRecord } from './types';

use([BarChart, LineChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer]);

export function AnalyticsChart(props: {
  option: EChartsCoreOption;
  height?: number;
  eventLookup?: ReadonlyMap<string, AnalyticsEventRecord>;
  onEventClick?: (event: AnalyticsEventRecord) => void;
}): React.JSX.Element {
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = container.current;
    if (element === null) {
      return undefined;
    }
    const chart = init(element, undefined, { renderer: 'canvas' });
    chart.setOption(props.option);
    function selectEvent(parameters: ECElementEvent): void {
      const eventId = readAnalyticsEventId(parameters.data);
      const event = eventId === null ? undefined : props.eventLookup?.get(eventId);
      if (event !== undefined) props.onEventClick?.(event);
    }
    function resize(): void {
      chart.resize();
    }
    if (props.onEventClick !== undefined) chart.on('click', selectEvent);
    window.addEventListener('resize', resize);
    return () => {
      chart.off('click', selectEvent);
      window.removeEventListener('resize', resize);
      chart.dispose();
    };
  }, [props.eventLookup, props.onEventClick, props.option]);

  return <div ref={container} className="analytics-chart" style={{ height: props.height ?? 320 }} />;
}

function readAnalyticsEventId(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || !('analyticsEventId' in value)) {
    return null;
  }
  return typeof value.analyticsEventId === 'string' ? value.analyticsEventId : null;
}
